import React, { useState } from "react";
import IngredientsModal from "./IngredientsModal";
import TipsModal from "./TipsModal";
import TextBlockLoader from "../loaders/TextBlockLoader";

export default function RecipeInstructions({ instructions, loading }) {
  const [activeStep, setActiveStep] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState([]);

  const highlightText = (text) => {
    return text
      // Highlight time durations (e.g., "30 to 50 minutes", "2-4 hours")
      .replace(
        /(\b\d+\s*(more\s*)?(-|to|–)?\s*\d*\s*(seconds?|minutes?|hours?)\b)/gi,
        (match) => `<strong class="font-bold text-gray-800">${match}</strong>`
      )
      // Highlight temperature values (e.g., "400°F", "180 degrees Celsius")
      .replace(
        /(\b\d{2,4}\s*(?:[-–to]\s*\d{2,4})?\s*(?:degrees|°)?\s*(?:Celsius|C|Fahrenheit|F)\b)/gi,
        (match) => `<strong class="font-bold text-red-700">${match}</strong>` // 🔥 Red for temperatures
      )
      .split(/(<strong.*?<\/strong>)/)
      .map((part, i) =>
        part.startsWith("<strong") ? (
          <span key={i} dangerouslySetInnerHTML={{ __html: part }} />
        ) : (
          part
        )
      );
  };


  return (
    <div className="mb-6">
      <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Instructions</h3>

      {loading ? (
        <TextBlockLoader />
      ) : instructions?.length > 0 ? (
        <div className="space-y-4">
          {instructions.map((phase, index) => {
            const isExpanded = expandedPhases.includes(index);

            return (
              <div key={index} className="p-4 bg-white border border-gray-300 rounded-lg shadow-sm">
                {/* Phase Header with Collapsible Toggle */}
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() =>
                    setExpandedPhases((prev) =>
                      isExpanded ? prev.filter((phaseIndex) => phaseIndex !== index) : [...prev, index]
                    )
                  }
                >
                  <h4 className="text-md font-semibold text-gray-700">
                    {phase.name || `Step ${index + 1}`}
                  </h4>
                  <span className="text-gray-500 font-semibold transition hover:font-bold">
                    {isExpanded ? "-" : "+"}
                  </span>
                </div>

                {/* Steps List (Collapsible Content) */}
                {isExpanded && (
                  <div className="mt-3">
                    {/* Icons for Ingredients and Tips */}
                    <div className="flex items-center space-x-4 mb-3">
                      {phase.ingredients?.length > 0 && (
                        <IngredientsModal ingredients={phase.ingredients} phaseName={phase.name || `Step ${index + 1}`} />
                      )}
                      {phase.tips?.length > 0 && (
                        <TipsModal tips={phase.tips} phaseName={phase.name || `Step ${index + 1}`} />
                      )}
                    </div>

                    {/* Steps */}
                    <ol className="list-decimal list-inside space-y-4">
                      {phase.steps.length > 0 ? (
                        phase.steps.map((step, stepIndex) => (
                          <li
                            key={stepIndex}
                            className={`text-sm text-gray-700 transition-all ${activeStep === `${index}-${stepIndex}`
                              ? 'bg-green-50 border-l-4 border-green-600 pl-4'
                              : ''
                              }`}
                            onMouseEnter={() => setActiveStep(`${index}-${stepIndex}`)}
                            onMouseLeave={() => setActiveStep(null)}
                          >
                            {highlightText(step)}
                          </li>
                        ))
                      ) : (
                        <p className="text-gray-500">No steps available.</p>
                      )}
                    </ol>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-500">No instructions available.</p>
      )}
    </div>
  );
}
